/** @format */

import React from "react";
import { TGiornoPresenze } from "../../../../redux-modules/declarations/maps/presenzeVista";
import { tinta } from "../format";
import { lineaDelGiorno, sfondoOre, TFasciaMinuti } from "../linea";

interface Props {
  giorno: TGiornoPresenze;
  fascia: TFasciaMinuti;
  adesso: string | null;
  /** larghezza stimata della linea, per accorciare le etichette */
  pixel: number;
  compatta?: boolean;
  /** con onApri ogni blocco è un pulsante che apre la giornata della persona */
  onApri?: () => void;
  nomePersona?: string;
}

/** Linea oraria di un giorno: turni previsti, permessi a ore, timbrature e "adesso". */
export const LineaGiorno = ({
  giorno,
  fascia,
  adesso,
  pixel,
  compatta = false,
  onApri,
  nomePersona,
}: Props): JSX.Element => {
  const linea = lineaDelGiorno(giorno, fascia, adesso, pixel);
  const classe = `linea${compatta ? " compatta" : ""}${giorno.festivita ? " festa" : ""}`;
  const suffisso = nomePersona ? `, apri la giornata di ${nomePersona}` : "";

  const elemento = (
    chiave: string,
    className: string,
    style: React.CSSProperties,
    titolo: string,
    contenuto: React.ReactNode,
  ): JSX.Element =>
    onApri ? (
      <button
        key={chiave}
        type="button"
        className={className}
        style={style}
        title={titolo}
        aria-label={titolo + suffisso}
        onClick={onApri}
      >
        {contenuto}
      </button>
    ) : (
      <span key={chiave} className={className} style={style} title={titolo}>
        {contenuto}
      </span>
    );

  return (
    <div
      className={classe}
      style={{ backgroundSize: sfondoOre(fascia) }}
      title={giorno.festivita ? `Festività: ${giorno.festivita}` : undefined}
    >
      {linea.intera &&
        elemento(
          "intera",
          `giornata-intera ${linea.intera.classe}`,
          {
            // dopo la notte prima, o dopo il segno "‹ 06:03" quando la notte è fuori dalla fascia
            ...(linea.intera.sinistra > 0
              ? { left: `calc(${linea.intera.sinistra}% + 4px)` }
              : linea.fuori.some(f => f.lato === "sinistra")
              ? { left: 62 }
              : {}),
            ...(linea.intera.colore
              ? {
                  background: tinta(linea.intera.colore, 0.2),
                  borderColor: tinta(linea.intera.colore, 0.65),
                }
              : {}),
          },
          linea.intera.etichetta,
          <>
            {linea.intera.colore && (
              <span className="pallino" style={{ background: linea.intera.colore }} />
            )}
            {linea.intera.etichetta}
          </>,
        )}
      {linea.blocchi.map(b =>
        elemento(
          b.chiave,
          b.classe,
          {
            left: `${b.sinistra}%`,
            width: `${b.larghezza}%`,
            ...(b.fondo ? { backgroundColor: b.fondo, borderColor: b.bordo } : {}),
          },
          b.titolo,
          <>
            {b.viva && <span className="punto-vivo" />}
            {b.etichetta}
          </>,
        ),
      )}
      {linea.fuori.map(f =>
        elemento(`fuori-${f.lato}`, `fuori ${f.lato}`, {}, f.titolo, f.etichetta),
      )}
      {linea.adesso !== null && <span className="adesso" style={{ left: `${linea.adesso}%` }} />}
    </div>
  );
};
